/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl, SafeValue } from '@angular/platform-browser';
import { DropdownModel } from '../../models';
import { Injectable, SecurityContext } from '@angular/core';
import { L7GlobalServiceModel } from 'src/app/modules/loadbalancer/shared/models/l7.global.service.model';
import { LanguageService } from './language.service';
import { LogService } from './log.service';
import { Md5 } from 'ts-md5';
import { ValidationService } from '../component/validation/validation.service';
import { isArray } from 'chart.js/helpers';
import { EN_US } from '../../languages';

@Injectable()
export class UtilsService {
    constructor(
        private languageService: LanguageService,
        private logService: LogService,
        private sanitizer: DomSanitizer
    ) {
    }

    /**
     * Debounce timer ID, needs to be class level as the function needs to reference it.
     * @private
     */
    private debounceTimerId = {};

    /**
     * Remove HTML tags from a string and clean white space.
     * @param string
     */
    public removeHTMLFromString(string: string): string {
        return string.replace(/<\/?[^>]+(>|$)/g, '').replace(/^\s+|\s+$/gm, '');
    }

    /**
     * Convert string to hyphen seperated string for use as ID. Using '/g' for multiple matches.
     * @param string
     */
    public convertStringToId(string: string): string {
        let stringToConvert = string;
        // remove first and last slashes if url path
        stringToConvert = stringToConvert.replace(/^\/|\/$/g, '');
        // Check if string is camelcase (doesn't contain spaces) and not all in capitol letters.
        if (!stringToConvert.includes(' ') && !/^[A-Z]*$/.test(stringToConvert)) {
            stringToConvert = stringToConvert.replace(/([a-z0-9\w])([A-Z])/g, '$1 $2');
        }

        return stringToConvert.toLowerCase().replace(/[\s_\/]/g, '-');
    }

    /**
     * Check if the value is a service ID.
     * @param value
     */
    public isServiceId(value: string): boolean {
        const regexPattern = /[a-zA-Z0-9]{3}-[a-zA-Z0-9]{3}-[a-zA-Z0-9]{3}-[a-zA-Z0-9]{3}/;

        return regexPattern.test(value);
    }

    /**
     * Convert label to singular, optional index.
     * @param name
     * @param index
     */
    public convertNameToSingular(name: string, index?: number): string {
        const nameMinusS: string = name.endsWith('ies')
            ? name.replace('ies', 'y')
            : name.endsWith('s') ? name.slice(0, -1) : name;
        const formattedName: string = nameMinusS.endsWith('\'') ? nameMinusS.slice(0, -1) : nameMinusS;

        return index ? formattedName + ' ' + index : formattedName;
    }

    /**
     * Get heading (try and match translation).
     * @param name
     */
    public tryToMatchNameToLanguageFile(name: string): string {
        const keyIndex: number = Object.keys(EN_US).findIndex((item: string): boolean => item === name);
        const valueIndex: number = Object.values(EN_US).findIndex((item: string): boolean => item.toLowerCase() === name.toLowerCase());
        if (keyIndex !== -1) {
            return this.languageService.translate(Object.keys(EN_US)[keyIndex]);
        } else if (valueIndex !== -1) {
            return this.languageService.translate(Object.keys(EN_US)[valueIndex]);
        } else {
            this.logService.debug('UtilsService:tryToMatchNameToLanguageFile', 'Can\'t match ', name);
        }

        return name;
    }

    /**
     * Filter an array by property for duplicate check.
     * @param array
     * @param property
     * @param editObject
     */
    public filterPropertyForDuplicateCheck(array: any[], property: string, editObject?: any): string[] {
        const editHasProperty = editObject ? !!editObject[property] : false;
        const arrayToMap = editHasProperty ? array.filter(item => item[property] !== editObject[property]) : array;

        return arrayToMap?.map((item: any) => item[property]?.toLowerCase());
    }

    /**
     * Find duplicates in array.
     * @param array
     */
    public findDuplicates(array: any[]): any[] {
        return array.filter((item, index: number) => array.indexOf(item) !== index);
    }

    /**
     * Find duplicates in array.
     * @param array
     */
    public findDuplicatesObjects(array: any[]): any[] {
        const flatObjects = array.map(item => JSON.stringify(item));

        return flatObjects.filter((item, index: number) => flatObjects.indexOf(item) !== index);
    }

    /**
     * Loops through an array of strings or numbers and counts duplicates.
     * @param array
     */
    public countArrayDuplicates(array: any): any {
        const counts = {};
        array.forEach((item: string | number) => {
            counts[item] = (counts[item] || 0) + 1;
        });

        return counts;
    }

    /**
     * Get count ID for the most or least used from the counts object.
     * @param counts
     * @param sortBy
     */
    public getCountId(counts: any, sortBy: 'most' | 'least'): string {
        const sortedCounts = this.sortArray(Object.values(counts));
        const counter = sortBy === 'most' ? sortedCounts.reverse()[0] : sortedCounts[0];
        for (const count in counts) {
            if (counts.hasOwnProperty(count)) {
                if (counts[count] === counter) {
                    return count;
                }
            }
        }
    }

    /**
     * Alternative to the native findIndex - starts at end of array and works backwards.
     * @param array
     * @param objectProperty
     * @param value
     * NOTE don't try and use array.reverse() as this changes the indexes.
     */
    public findIndexReversed(array: any[], objectProperty: string, value?: string) {
        // This needs to be in reverse, so we can't use other array methods.
        for (let index = array.length - 1; index >= 0; index--) {
            const item = array[index];
            if (item.hasOwnProperty(objectProperty)) {
                if (value && item[objectProperty] === value) {
                    return index;
                } else if (!value && item[objectProperty]) {
                    return index;
                }
            }
        }

        return -1;
    }

    /**
     * Deep duplicate an array so there is no reference to the original.
     * @param oldArray
     */
    public duplicateArray(oldArray: object[]): any {
        const newArray: any = [];

        oldArray.forEach((item) => {
            newArray.push(Object.assign({}, item));
        });

        return newArray;
    }

    /**
     * Compare two arrays
     * @param array1
     * @param array2
     */
    public compareArrays(array1: any[], array2: string | any[]) {
        return Array.isArray(array1) &&
            Array.isArray(array2) &&
            array1.length === array2.length &&
            array1.every((val, index) => val === array2[index]);
    }

    /**
     * Compare two sets of data.
     * @param obj1
     * @param obj2
     * @private
     */
    public compareData(obj1: any, obj2: any): boolean {
        return JSON.stringify(obj1) === JSON.stringify(obj2);
    }

    /**
     * Find difference between two objects and return the different properties.
     * @param object1
     * @param object2
     */
    public findDifference(object1: any, object2: any): string[] {
        const diffKeys = Object.keys(object1)
            .reduce((acc, key) => {
                if (!(key in object2) ||
                    object1[key] !== object2[key]) {
                    acc.push(key);
                }

                return acc;
            }, []);

        Object.keys(object2).forEach(key => {
            if (!(key in object1) ||
                object1[key] !== object2[key]) {
                if (!diffKeys.includes(key)) {
                    diffKeys.push(key);
                }
            }
        });

        return diffKeys;
    }

    /**
     * Loop through a list of properties and delete them.
     * @param object
     * @param properties
     */
    public deletePropertiesFromObject(object: any, properties: string[]): any {
        for (const property of properties) {
            if (object.hasOwnProperty(property)) {
                delete object[property];
            }
        }

        return object;
    }

    /**
     *  Deep duplicate an object so there is no reference to the original.
     * @param {Object} obj
     * @returns
     */
    public duplicateObject(obj: object): any {
        return JSON.parse(JSON.stringify(obj));
    }

    /**
     * Generate a random string of integers
     * @returns {string}
     */
    public generateRandomString(): string {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }

    /**
     * Generate an ID from a string
     * @param string
     */
    public generateUniqueId(string: string): string {
        return Md5.hashStr(string) + Math.random();
    }

    /**
     * Get UID for response.
     * @param response
     * @param message
     */
    public getResponseUId(response: any, message: string): string {
        return response.uId ? response.uId : this.generateUniqueId(message);
    }

    /**
     * First letter to uppercase
     * @param string
     */
    public firstLetterUppercase(string: string): string {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    /**
     * Calculate time from ago
     * @param current
     * @param previous
     * @param returnMessage
     */
    public timeDifference(current: any, previous: any, returnMessage: boolean = false) {
        const msPerMinute = 60 * 1000;
        const msPerHour = msPerMinute * 60;
        const msPerDay = msPerHour * 24;
        const msPerMonth = msPerDay * 30;
        const msPerYear = msPerDay * 365;

        const elapsed = current - previous;

        if (elapsed < msPerMinute) {
            const diff: number = Math.round(elapsed / 1000);

            return returnMessage ? diff + ' seconds ago' : diff + 's';
        } else if (elapsed < msPerHour) {
            const diff: number = Math.round(elapsed / msPerMinute);

            return returnMessage ? diff + ' minutes ago' : diff + 'm';
        } else if (elapsed < msPerDay) {
            const diff: number = Math.round(elapsed / msPerHour);

            return returnMessage ? diff + ' hours ago' : diff + 'h';
        } else if (elapsed < msPerMonth) {
            const diff: number = Math.round(elapsed / msPerDay);

            return returnMessage ? 'approximately ' + diff + ' days ago' : diff + 'd';
        } else if (elapsed < msPerYear) {
            const diff: number = Math.round(elapsed / msPerMonth);

            return returnMessage ? 'approximately ' + Math.round(elapsed / msPerMonth) + ' months ago' : diff + 'mon';
        } else {
            const diff: number = Math.round(elapsed / msPerYear);

            return returnMessage ? 'approximately ' + diff + ' years ago' : diff + 'y';
        }
    }

    /**
     * Get time difference between two dates in datetime format e.g. 2025-05-16T08:08:09+01:00
     * @param from
     * @param to
     */
    public getTimeDifference(from: any, to: any): any {
        const formatStartDate = typeof from === 'number' ? from : new Date(from).getTime();
        const formatEndDate = typeof to === 'number' ? to : new Date(to).getTime();

        const diff: number = formatEndDate - formatStartDate;
        const seconds: number = Math.floor(diff / 1000);
        const minutes: number = Math.floor(seconds / 60);
        const hours: number = Math.floor(minutes / 60);
        const days: number = Math.floor(hours / 24);

        return {
            seconds: seconds,
            minutes: minutes,
            hours: hours,
            days: days
        };
    }

    /**
     * Gets value from object. Performs a search on the object for the key path, prevents call on object path that
     * doesn't exist and returning an error.
     * Equivalent to condition: object && object.key1 && object.key1.key2
     * Usage: getValue(object, 'key1.key2')
     * Example: this.monitorForm.controls.rules['controls'][index].controls['numberOfReals']
     * becomes
     * this.utils.getObjectValue(this.monitorForm, `controls.rules[controls][${index}].controls[numberOfReals]`)
     * or
     * Example this.controllerState[serviceName][counter - 1].headers
     * becomes
     * this.utilsService.getObjectValue(this.controllerState, `${serviceName}[${counter - 1}].headers`);
     * note the removal of the square brackets around serviceName
     * @param object
     * @param propertiesPath
     * @returns any or undefined
     */
    public getObjectValue(object: any, propertiesPath: string): any {
        return propertiesPath
            // Remove square brackets from path e.g. [0]
            .replace(/\[/g, '.')
            .replace(/]/g, '')
            // Create an array from the path
            .split('.')
            // Reduce the array checking each part of the object path.
            .reduce((o, k) => (o || {})[k], object);
    }

    /**
     * Lowercase object keys
     * @param object
     */
    public toLowerCaseObjectKeys(object: Record<string, unknown>): Record<string, unknown> {
        if (!object) {
            return {};
        }

        return Object.keys(object).reduce((acc: any, key: string): any => {
            acc[key.toLowerCase()] = object[key];

            return acc;
        }, {} as Record<string, unknown>);
    }

    /**
     * Convert array or array of objects to drop-down key value pair.
     * @param array
     * @param propertyForKey
     * @param propertyForValue
     */
    public convertArrayToDropDown(array: any, propertyForKey = 'id', propertyForValue = 'value'): DropdownModel[] {
        const dropdown = [];
        if (array?.length) {
            for (const item of array) {
                const { key, value } = this.getKeyAndValue(item, propertyForKey, propertyForValue);
                if (key && value) {
                    dropdown.push(<DropdownModel>{ key, value, });
                } else {
                    this.logService.warn(
                        'UtilsService: convertArrayToDropDown',
                        'An item for the dropdown was in the incorrect format.'
                    );
                }
            }
        }

        return dropdown;
    }

    /**
     * Gte key and value from item.
     * @param item
     * @param keyToUse
     * @param valueToUse
     * @private
     */
    private getKeyAndValue(item: any, keyToUse: string, valueToUse: string): DropdownModel {
        let returnKey: string, returnValue: string;
        const key = item[keyToUse] ? item[keyToUse] : item.id ? item.id : item;
        const value = item[valueToUse] ? item[valueToUse] : item.value ? item.value : item;
        if ((typeof key !== 'object' && !Array.isArray(key)) &&
            (typeof value !== 'object' && !Array.isArray(value))) {
            returnKey = key;
            returnValue = value;
        } else {
            if (!Array.isArray(key) && Object.keys(item).length === 1) {
                returnKey = item[Object.keys(item)[0]];
                returnValue = item[Object.keys(item)[0]];
            } else if (Array.isArray(key) && key.length === 1) {
                returnKey = item[0];
                returnValue = item[0];
            }
        }

        return { key: returnKey, value: returnValue };
    }

    /**
     * Returns form control as FormControl.
     * @param control
     */
    public getFormControl(control: any): FormControl {
        return control as FormControl;
    }

    /**
     * Marks all controls in a form group as touched
     * @param formGroup - The form group to touch
     */
    public markFormGroupTouched(formGroup: FormGroup): void {
        (<any>Object).values(formGroup.controls).forEach((control: FormGroup): void => {
            control.markAsDirty();
            control.markAsTouched();

            // Catch any embedded controls
            if (control.controls) {
                this.markFormGroupTouched(control);
            }
        });
    }

    /**
     * Validate form against the initial data object
     * We can use this, so we don't make unnecessary api calls.
     * @param initialFormData
     * @param newFormData
     * @param exclude
     */
    public hasFormDataChanged(initialFormData: any, newFormData: any, exclude?: any[]): boolean {
        if ((initialFormData && Object.keys(initialFormData).length) && (newFormData && Object.keys(newFormData).length)) {
            const changes: any[] = [];
            for (const property in newFormData) {
                if (newFormData.hasOwnProperty(property)) {
                    if (Array.isArray(newFormData[property])) {
                        if (!this.compareArrays(initialFormData[property], newFormData[property])) {
                            changes.push(property);
                        }
                    } else if (typeof newFormData[property] === 'object' && newFormData[property] !== null) {
                        this.logService.debug(
                            'validateFormAgainstInitialDataObject',
                            `The form might not being evaluated correctly as '${property}' is an object`);

                    } else if (initialFormData.hasOwnProperty(property)) {
                        if (newFormData[property] !== initialFormData[property]) {
                            changes.push(property);
                        }
                    } else if (exclude && exclude.includes(property) && newFormData[property] !== null) {
                        changes.push(property);
                    } else {
                        this.logService.debug(
                            'validateFormAgainstInitialDataObject',
                            `The form might not being evaluated correctly as '${property}' is null`);
                    }
                }
            }

            return !!changes.length;
        } else {
            this.logService.debug('validateFormAgainstInitialDataObject', 'initialFormData hasn\'nt been set correctly.');

            return true;
        }
    }

    /**
     * Detect chrome autofilled inputs and clear the validation.
     * @param formGroup
     */
    public clearValidatorOnAutoFill(formGroup: FormGroup): void {
        const userAgent = navigator.userAgent;
        if (userAgent.match(/chrome|chromium|crios/i)) {
            const autoCompleteFields = document.querySelectorAll('input:-internal-autofill-selected');
            if (autoCompleteFields.length) {
                const inputsArray: Element[] = Array.from(autoCompleteFields);
                for (const input of inputsArray) {
                    const formElement = formGroup.get(input.id);
                    if (formElement?.enabled) {
                        formElement.markAsTouched();
                        formElement.markAsDirty();
                        formElement.clearValidators();
                        formElement.updateValueAndValidity();
                    }
                }
            }
        }
    }

    /**
     * Create an array if it does not exist
     * @param arr
     * @param name
     */
    public createArrayIfNotExist(arr: any, name: string): void {
        if (!arr[name]) {
            arr[name] = [];
        }
    }

    /**
     * Takes HaProxyStatus or Bear socket data and converts it to a user-friendly display name.
     * returns not available if no matches are found. Key pair match to get preferred friendly string.
     * @param status
     */
    public getStatusDisplayName(status: string): string {
        const displayStatus: { [key: string]: string; } = {
            'up': 'online',
            'online': 'online',
            'open': 'online',
            'active': 'online',
            'activating': 'activating',
            'inactive': 'inactive',
            'down': 'down',
            'stop': 'down',
            'unavailable': 'down',
            'warn': 'warning',
            'maint': 'maintenance',
            'maintenance': 'maintenance',
            'halt': 'maintenance',
            'nolb': 'maintenance',
            'restart': 'maintenance',
            'reload': 'maintenance',
            'drain': 'drain',
            'suspicious': 'suspicious',
            'passive': 'passive',
            'error': 'error',
            'failed': 'error',
            'disabled': 'disabled',
            'pending': 'pending',
            'failure': 'failure'
        };

        return displayStatus[status?.toLowerCase()] || 'N/A';
    }

    /**
     * Debounce is a self clearing timer function.
     * Each time the function is called it clears its own timer,
     * only when the function has stopped being called does it complete and call the callback
     * @param timerId
     * @param callBack
     * @param delay (optional)
     */
    public debounce(timerId: string, callBack: () => void, delay = 250) {
        if (this.debounceTimerId[timerId]) {
            clearTimeout(this.debounceTimerId[timerId]);
        }
        this.debounceTimerId[timerId] = setTimeout(() => {
            callBack();
            delete this.debounceTimerId[timerId];
        }, delay);
    }

    /**
     * Format captured error
     * @param error
     */
    public formatCapturedError(error: any): any {
        let formattedMessage: any = 'No message';
        let html: string;
        if (error.data) {
            const messageObject = this.getMessageFromResponse(error.data);
            if (messageObject) {
                // If message object has values then update formatted message.
                formattedMessage = this.formatResponseMessage(messageObject);
            }

            html = `Action: <strong>${error.data.section}</strong>, Title: <strong>${error.message}</strong><br>`;
        } else {
            formattedMessage = error.message;
        }

        if (formattedMessage !== null) {
            html = html + `<small>${formattedMessage}</small>`;
        }

        return html;
    }

    /**
     * Create a range
     * @param from
     * @param to
     */
    public createRangeArray(from: number, to: number): any[] {
        const input = [];
        for (let i = from; i >= to; i--) {
            input.push({
                key: i,
                value: i
            });
        }

        return input;
    }

    /**
     * Decode JWT to use internal properties.
     * @param {string} token
     * @returns {any}
     */
    public decodeJWT(token: string): any {
        const base64Url = token.split('.')[1];
        if (base64Url) {
            const base64 = base64Url.replace('-', '+').replace('_', '/');
            let data = '';
            try {
                data = JSON.parse(window.atob(base64));
            } catch (error) {
                this.logService.warn('utilsService: decodeJWT', 'Unable to parse base64', error);

                return null;
            }

            return data;
        } else {
            this.logService.warn('utilsService: decodeJWT', 'Unable to split token');

            return null;
        }
    }

    /**
     * Remove duplicates in an array
     * @param array
     */
    public dedupe(array: any[]): any {
        const primitives = { 'boolean': {}, 'number': {}, 'string': {} }, objs = [];

        return array.filter(item => {
            const type = typeof item;
            if (type in primitives) {
                return primitives[type].hasOwnProperty(item) ? false : (primitives[type][item] = true);
            } else {
                return objs.indexOf(item) >= 0 ? false : objs.push(item);
            }
        });
    }

    /**
     * Removes null, undefined and empty value in an array
     * Returns object with key length, any number and non-empty strings.
     * @param array
     */
    public cleanArray(array: any[]): any {
        return array.filter(
            item => (item !== null && item !== undefined) &&
                (
                    (typeof item === 'string' && item !== '') ||
                    (typeof item === 'object' && Object.keys(item).length) ||
                    (typeof item === 'number')
                )
        );
    }

    /**
     * Remove empty array items and trim white space off others.
     * @param valuesArray
     * @private
     */
    public removeSpecialCharactersFromArray(valuesArray: any[]): string[] {
        const cleanArray: string[] = [];
        for (const value of valuesArray) {
            if (typeof value === 'string') {
                if (value?.length > 0) {
                    const removedSpecialCharacters = value.replace(/[^a-zA-Z':._@/\- \d]/g, '');
                    cleanArray.push(removedSpecialCharacters.trim());
                }
            } else {
                cleanArray.push(value);
            }
        }

        return cleanArray;
    }

    /**
     * Convert search term into comma separated array.
     * @param value
     */
    public convertStringToArray(value: any): string[] {
        return value?.includes(',') ? value.split(',') : [value];
    }

    /**
     * Clean object, removes properties with null values and returns the edited object.
     * @param object
     */
    public cleanObject(object: any): any {
        for (const property in object) {
            if (object[property] === null) {
                delete object[property];
            }
        }

        return object;
    }

    /**
     * Helper function to sort numbers based on 10's
     * Example 1, 200, 40, 5 becomes 1, 5, 40, 200
     * @param a
     * @param b
     * @private
     */
    public sortNumbers(a: number, b: number): number {
        return a - b;
    }

    /**
     * Compare decimals.
     * @param firstNumber
     * @param secondNumber
     * @param operation
     */
    public compareDecimals(firstNumber: string, secondNumber: string, operation: string): boolean {
        const a: number = parseFloat(firstNumber);
        const b: number = parseFloat(secondNumber);

        if (isNaN(a) || isNaN(b)) {
            this.logService.warn('UtilsService:compareDecimals', 'Invalid number input');

            return false;
        }

        switch (operation) {
            case '<':
                return a < b;
            case '<=':
                return a <= b;
            case '>':
                return a > b;
            case '>=':
                return a >= b;
            case '===':
                return a === b;
            default:
                this.logService.warn('UtilsService:compareDecimals', `Unsupported operation: ${operation}`);

                return false;
        }
    }

    /**
     * Sort array
     * @param array
     */
    public sortArray(array: any[]) {
        return array.sort((a, b) => a - b);
    }

    /**
     * Re-order array by property
     * @param array
     * @param property
     * @param order - Specify the order the array should be sorted with e.g. [5,4,3,2,1]
     */
    public sortArrayByProperty(array: any[], property: string, order?: any[] | 'asc' | 'desc'): any {
        if (order) {
            if (isArray(order)) {
                return array.sort((a, b) => order.indexOf(a[property]) - order.indexOf(b[property]));
            } else {
                return this.sortArrayByPropertyAscDesc(array, property, order);
            }
        } else {
            return array.sort((a, b) => {
                if (a[property] > b[property]) {
                    return 1;
                } else if (b[property] > a[property]) {
                    return -1;
                }

                return 0;
            });
        }
    }

    /**
     * Sort an array by ascending/descending order of given property.
     * @param array
     * @param property
     * @param order
     */
    public sortArrayByPropertyAscDesc(array: any[], property: string, order: any[] | 'asc' | 'desc'): any {
        return array.sort((a, b) => order === 'asc' ? (a[property] - b[property]) : (b[property] - a[property]));
    }

    /**
     * Takes comma seperated string and replaces the last comma with 'and'
     * @param string
     * @param stringContainSpaces
     */
    public replaceLastCommaWithAnd(string: string, stringContainSpaces = false): string {
        const cleanedString = this.cleanStringForFormatting(string, stringContainSpaces);

        return cleanedString.replace(/,(?=[^,]*$)/, ' and');
    }

    /**
     * Takes comma seperated string and replaces the last comma with 'or'
     * @param string
     * @param stringContainSpaces
     */
    public replaceLastCommaWithOr(string: string, stringContainSpaces = false): string {
        const cleanedString = this.cleanStringForFormatting(string, stringContainSpaces);

        return cleanedString.replace(/,(?=[^,]*$)/, ' or');
    }

    /**
     * Helper function to clean string.
     * @param string
     * @param stringContainSpaces
     * @private
     */
    private cleanStringForFormatting(string: string, stringContainSpaces = false): string {
        const cleanedString = string.replace(/ or | and /g, ',');
        const removedSpaces = stringContainSpaces ? cleanedString : cleanedString.replace(/\s/g, '');

        return removedSpaces.replace(/,/g, ', ');
    }

    /**
     * Fix for missing type of for array
     * @param value
     */
    public typeOfIncludingArray(value: any): string {
        if (Array.isArray(value)) {
            return 'array';
        } else {
            return typeof value;
        }
    }

    /**
     * Reduces the connection number to 1k if over thousand and 1m if over million
     * @param connections
     */
    public formatConnectionSize(connections: number): number | string {
        if (connections > 1000000) {
            return connections / 1000000 + 'm';
        } else if (connections > 1000) {
            return connections / 1000 + 'k';
        }

        return connections;
    }

    /**
     * Scroll into view if its position is lower than the page height.
     * @param selector
     */
    public scrollToElement(selector: string): void {
        const element = document.querySelector(selector);
        if (!element) {
            return;
        }

        const elementTop = element.getBoundingClientRect().top;
        // If already in view don't scroll.
        if (elementTop > window.innerHeight) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    }

    /**
     * Excepts JS new Date(); as date parameter.
     * @param date
     */
    public formatTimeForHtmlInput(date: any | Date): string {
        if (typeof date === 'string' && !date.includes('T') && date.includes(':')) {
            const time = date.split(':');

            return `${time[0].padStart(2, '0')}:${time[1].padStart(2, '0')}`;
        } else if (typeof date === 'string' && date.includes('T')) {
            const dateParts = date.split('T');
            const time = dateParts[1].split(':');

            return time[0] + ':' + time[1];
        } else {
            const hours = date.getHours();
            const minutes = date.getMinutes();

            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
    }

    /**
     * Makes the time and date look nice in the table.
     * @param isoDate
     * @param includeTime
     */
    public createDateTimeString(isoDate: string | number | Date, includeTime = true): string {
        const dateTime = new Date(isoDate);
        const locale = Intl.DateTimeFormat().resolvedOptions().locale;

        return dateTime.toLocaleDateString(locale) + (includeTime ? ' ' + dateTime.toLocaleTimeString(locale) : '');
    }

    /**
     * Return a date / time object from a given iso date string
     * @param isoDateTime
     */
    public getDateTimeObjectFromIsoDateTime(isoDateTime: string): any {
        const dateTimeParts = isoDateTime.split('T');
        // Account for the +/- timezone shift; iso date/time format is always the same. Use slice.
        const time = dateTimeParts[1].slice(0, 5);
        const timeOffset = dateTimeParts[1].slice(8);

        return {
            date: dateTimeParts[0],
            time: time,
            timeOffset: timeOffset
        };
    }

    /**
     * Calculate the time elapsed to now in a human-readable format.
     * @param date
     * @param justNowCutOff - default 5 (seconds) to show 'Just now' message
     */
    public calculateElapsedTimeToNow(date: number | Date, justNowCutOff = 5): any {
        //@ts-ignore
        const seconds = Math.floor((new Date() - date) / 1000);
        let number: number;
        let interval = seconds / 31536000;
        if (interval > 1) {
            number = Math.floor(interval);

            return {
                number: number,
                duration: (number > 1 ? 'years' : 'year')
            };
        }
        interval = seconds / 2592000;
        if (interval > 1) {
            number = Math.floor(interval);

            return {
                number: number,
                duration: (number > 1 ? 'months' : 'month')
            };
        }
        interval = seconds / 86400;
        if (interval > 1) {
            number = Math.floor(interval);

            return {
                number: number,
                duration: (number > 1 ? 'days' : 'day')
            };
        }
        interval = seconds / 3600;
        if (interval > 1) {
            number = Math.floor(interval);

            return {
                number: number,
                duration: (number > 1 ? 'hours' : 'hour')
            };
        }
        interval = seconds / 60;
        if (interval > 1) {
            number = Math.floor(interval);

            return {
                number: number,
                duration: (number > 1 ? 'minutes' : 'minute')
            };
        }

        if (seconds > justNowCutOff) {
            return {
                number: Math.floor(seconds),
                duration: 'seconds'
            };
        }

        return {
            number: 0,
            duration: 'date_just_now'
        };
    }

    /**
     * Validates confirm password against password, sets error on input and marks as touched.
     * @param password
     * @param confirmPassword
     * @param confirmPasswordInput
     */
    public validatePasswordWithConfirmPassword(
        password: string,
        confirmPassword: string,
        confirmPasswordInput: AbstractControl<any, any>
    ): void {
        if (confirmPassword !== password && !confirmPasswordInput.hasError('confirmMismatch')) {
            confirmPasswordInput.setErrors({ 'confirmMismatch': `${this.languageService.translate('password_mismatch')}` });
            if (confirmPassword && confirmPassword.length) {
                this.debounce('confirmPassword', () => {
                    confirmPasswordInput.markAsTouched();
                }, 500);
            }
        }
    }

    /**
     * Sanitize url for external content.
     * @param url
     */
    public sanitiseExternalUrl(url: string): SafeResourceUrl {
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    /**
     * Sanitise a user value before processing.
     * @param value
     */
    public sanitizeInput(value: string): SafeValue {
        return this.sanitizer.sanitize(SecurityContext.HTML, value);
    }

    /**
     * Test is url string is actually a valid url by converting it into url object
     * @param urlString
     */
    public validateUrl(urlString: string) {
        let url: URL;
        try {
            url = new URL(urlString);
        } catch (_) {
            return false;
        }

        return !!url.href;
    }

    /**
     * Validate certs form label.
     * @param field
     * @param array
     * @param ignoreValue
     */
    public validateCertsFormLabel(field: AbstractControl<any, any>, array: any[], ignoreValue?: string): void {
        const value = field.value;
        const duplicateFound = array.find(item => item.name === value);

        if (duplicateFound) {
            if (ignoreValue && ignoreValue === duplicateFound.name) {
                field.setErrors(null);
            } else {
                field.setErrors({ 'duplicateLabel': `${this.languageService.translate('duplicate_certificate_label')}` });
            }
        }
    }

    /**
     * Get HA Proxy Stats Url.
     * @param l7GlobalObj
     */
    public getHaProxyStatsUrl(l7GlobalObj: L7GlobalServiceModel): string {
        if (l7GlobalObj.haproxyStatsEnable === false) {
            return undefined;
        }

        const protocol: string = l7GlobalObj.haproxyStatsSSL === true ? 'https://' : 'http://';
        const port = l7GlobalObj.haproxyStatsPort;
        const localData = JSON.parse(localStorage.getItem('lbHistory'));

        return protocol + localData.ip + ':' + port;
    }

    /**
     * Get lead IP.
     * @param selectedGroup
     * @private
     */
    public getIpLead(selectedGroup: any): string {
        if (ValidationService.ipV4Check(selectedGroup.networkIp)) {
            const parts: any = selectedGroup.networkIp.split('.');
            const slice: number = this.getSlice(selectedGroup.networkMask) || parts.length;

            return parts.slice(0, slice).join('.') + (slice === parts.length ? '' : '.');
        } else if (ValidationService.ipV6Check(selectedGroup.networkIp)) {
            const parts: any = selectedGroup.networkIp.split(':');

            return parts.slice(0, 4).join(':');
        }
    }

    /**
     * Get slice.
     * @param networkMask
     * @private
     */
    public getSlice(networkMask: number): number {
        if (networkMask >= 17 && networkMask <= 30) {
            return 3;
        } else if (networkMask >= 8 && networkMask <= 16) {
            return 2;
        }
    }

    /**
     * Get file extension from full filename
     * Allows for multiple dots in a filename
     * for example: domain.co.uk.pfx
     * @param fileName
     */
    public getExtensionFromFilename(fileName: string): string | null {
        if (!fileName) {
            return null;
        }

        const fileParts: string[] = fileName.split('.');
        if (fileParts.length > 1) {
            return fileParts[fileParts.length - 1].toLowerCase();
        }

        this.logService.error('UtilsService: getExtensionFromFilename', 'No file extension found!');

        return null;
    }

    /**
     * Convert array of objects to CSV.
     * @param data
     */
    public convertArrayOfObjectToCSV(data: any): string {
        if (data && data.length) {
            const headers = Object.keys(data[0]);
            const values = [];

            for (const row of data) {
                const rowValues = Object.values(row);
                const cleanRows = rowValues.map(item => this.convertItemToString(item));
                values.push(cleanRows);
            }

            const csvString = [headers, ...values];

            return csvString.map(item => item.join(',')).join('\n');
        }

        return data;
    }

    /**
     * Convert item to string.
     * @param item
     */
    public convertItemToString(item: any): string | number {
        if (typeof item === 'string') {
            return item.replace(/,/g, ';');
        } else if (Array.isArray(item)) {
            return item.toString().replace(/,/g, ' ');
        } else if (typeof item === 'object') {
            return JSON.stringify(item).replace(/,/g, ' ');
        } else {
            return item;
        }
    }

    /**
     * Get protocol type and version number.
     * @param value
     */
    public getCipherSecurityVersion(value: string): number {
        const splitVersion = value.split('v');

        if (splitVersion.length > 1) {
            return parseFloat(splitVersion[1].replace(/[^0-9.]+/g, ''));
        }
    }

    /**
     * Recursive helper function that drives into the response object to find a message
     * @param data
     * @private
     */
    private getMessageFromResponse(data: any): any {
        // Set up formatted errors.
        const formattedErrors = [];
        if (data.error && data.error.length) {
            for (const error of data.error) {
                // For each error push the needed values to the formatted errors array.
                formattedErrors.push({
                    message: error.message,
                    field: error.field,
                    type: error.type
                });
            }

            return formattedErrors;
        }
    }

    /**
     * Format the response message with HTML line breaks
     * @param messageObject
     * @private
     */
    private formatResponseMessage(messageObject: any) {
        // Set up formatted response.
        let formattedResponse = '';
        for (const message of messageObject) {
            // For each message in the message object check that field, message & type exist and have a length.
            if (message.field && message.field.length && message.message && message.message.length && message.type && message.type.length) {
                // Update the formatter response by adding the new HTML to the formatted response that has already been stored.
                formattedResponse = formattedResponse +
                    `<br><code>Message: ${message.message}</code><br>
                    <code>Field: ${message.field}</code><br>
                    <code>Type: ${message.type}</code><br>`;
            } else {
                // Update the formatter response by adding the new HTML to the formatted response that has already been stored.
                // If field, message or type does not exist or have a length then return the message using JSON Stringify.
                formattedResponse = formattedResponse + `<code>${JSON.stringify(message)}</code><br>`;
            }
        }

        return formattedResponse;
    }

    /**
     * Get version number from full version string.
     * @param versions
     */
    public getVersionNumberFromFullVersionString(versions: any[] | string): any {
        const regex = new RegExp(/^([0-9.]*)\./);
        if (typeof versions === 'string') {
            return parseInt(versions.match(regex)[1].replace(/\./g, ''), 10);
        } else {
            return versions.map(item => parseInt(item.match(regex)[1].replace(/\./g, ''), 10));
        }
    }

    /**
     * Takes V8 version numbers and returns true if A is less than B.
     * @param versionNumberA
     * @param versionNumberB
     */
    public versionNumberAIsLessThanB(versionNumberA: string, versionNumberB: string): boolean {
        if (versionNumberA && versionNumberB) {
            const versionPartsA: any = versionNumberA.split('.');
            const versionPartsB: any = versionNumberB.split('.');

            return versionPartsA.some((item: string, index: number): boolean =>
                parseInt(item, 10) < parseInt(versionPartsB[index], 10)
            );
        }

    }

    /**
     * Get the smallest number in array.
     * @param numbers
     */
    public getSmallestNumberInArray(numbers: number[]): number {
        return Math.min(...numbers);
    }

    /**
     * Check is array contains object with nested objects.
     * @param array
     */
    public arrayContainsNestedObjects(array: any[]): boolean {
        return array.some(item => {
            const values = Object.values(item);

            return values.some(objectItem => {
                if (Array.isArray(objectItem)) {
                    return false;
                } else {
                    return typeof objectItem === 'object';
                }
            });
        });
    }

    /**
     * Check duplicate form value.
     * @param formGroupField
     * @param fieldName
     * @param listToCheck - array of object only 1 level deep
     *      e.g. [{id: 1}] but [{id: {value: 1}}] will not give correct results if checking on value.
     * @param valueToCheck
     * @param editId
     */
    public checkDuplicateFormValue(
        formGroupField: AbstractControl,
        fieldName: string,
        listToCheck: any[],
        valueToCheck: string,
        editId: string | number
    ): void {
        if (this.arrayContainsNestedObjects(listToCheck)) {
            this.logService.warn(
                'UtilsService:checkDuplicateFormValue',
                'listToCheck contains nested objects so may give unexpected results, you may need to flatten your object.'
            );
        }

        if (this.checkForDuplicateValue(listToCheck, fieldName, valueToCheck, editId)) {
            formGroupField.setErrors({
                'duplicateName': `${this.languageService.translate('duplicate_already_in_use')}`
            });
            formGroupField.markAsTouched();
        } else if (formGroupField.hasError('duplicateName')) {
            formGroupField.setErrors({ 'duplicateName': null });
            formGroupField.updateValueAndValidity();
            formGroupField.markAsUntouched();
        }
    }

    /**
     * Check name for duplicate.
     * @param listToCheck
     * @param fieldName
     * @param valueToCheck
     * @param editId
     * @private
     */
    private checkForDuplicateValue(listToCheck: any[], fieldName: string, valueToCheck: string, editId: string | number): boolean {
        const match = listToCheck.find(item => item.id === editId);

        return listToCheck.some(
            item => item[fieldName].toString().toLowerCase() === valueToCheck.toString().toLowerCase() &&
                this.checkMatch(match, item, fieldName)
        );
    }

    /**
     * If in edit mode don't match the current value.
     * @param match
     * @param item
     * @param fieldName
     * @private
     */
    private checkMatch(match: any, item: any, fieldName: string): boolean {
        if (!match) {
            return true;
        } else {
            return item[fieldName] !== match[fieldName];
        }
    }

    /**
     * Convert base64 to blob.
     * @param b64Data
     * @param contentType
     * @param sliceSize
     * @private
     */
    public base64toBlob(b64Data: string, contentType = '', sliceSize = 512): Blob {
        const byteCharacters: string = atob(b64Data);
        const byteArrays: any[] = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice: string = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers: any[] = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray: Uint8Array = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: contentType });
    }

    /**
     * Get field value from custom field array (support tickets).
     * @param {any[]} customFields
     * @param {string} fieldName
     * @returns {string}
     * @private
     */
    public getCustomFieldValue(customFields: any[], fieldName: string): string {
        const item: any = customFields.find((item: any): boolean => item?.name === fieldName);

        return item?.value ? item?.value : 'n/a';
    }

    /**
     * Clean phone number by removing + or it being prefixed with the country code.
     * @param phoneNumber
     * @param countryCode
     */
    public cleanPhoneNumber(phoneNumber: string, countryCode: string): string {
        let formattedPhoneNumber: string = phoneNumber;
        if (phoneNumber.startsWith(countryCode)) {
            formattedPhoneNumber = phoneNumber.replace(countryCode, '');
        }

        if (phoneNumber.startsWith('+')) {
            formattedPhoneNumber = phoneNumber.replace('+', '');
        }

        return formattedPhoneNumber;
    }

    /**
     * Local storage is present.
     */
    public localStoragePresent(): boolean {
        const test = 't141-ls-test';
        try {
            window.localStorage.setItem(test, test);
            window.localStorage.removeItem(test);

            return true;
        } catch (e) {
            return false;
        }
    }

    /**
     * Add service layer type to a frontend/backend service.
     * @param services
     * @param type
     * @param name
     */
    public addTypeToService(services: any[], type: string, name: string): any {
        services.forEach((item): void => {
            const templateType: string = item.type === 'monitor' ? 'monitor' : type;
            const friendlyName: string = item.type === 'monitor' ? 'Monitor' : name;
            item['templateType'] = templateType;
            item ['templateTypeFriendlyName'] = friendlyName;
        });

        return services;
    }
}
